import React from 'react';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { CUADRILLA } from './media';
import { useSectionMotion, useHoverDireccional, EASE, REPITE } from './motion';
import Frase from './Frase';
import ReveladoFluido from './ReveladoFluido';
import { useIdioma, type Traducir } from '../../i18n/idioma';

/* Claves + URL. Las URL no se traducen. */
const SERVICIOS = [
    { clave: 'lp.academy.s1', url: 'https://www.razaperronaacademy.com/copy-of-website-2' },
    { clave: 'lp.academy.s2', url: 'https://www.razaperronaacademy.com/general-8' },
    { clave: 'lp.academy.s3', url: 'https://dechalanaempresario.com/landing' },
    { clave: 'lp.academy.s4', url: 'https://www.razaperronaacademy.com/category/all-products' },
    { clave: 'lp.academy.s5', url: 'https://www.razaperronaacademy.com/copy-of-website' },
    { clave: 'lp.academy.s6', url: 'https://www.razaperronaacademy.com/copy-of-website-3' },
] as const;

/**
 * Raza Perrona Academy — la casa de la que sale Raza Pro.
 *
 * Antes eran seis tarjetas idénticas con su ícono en una caja redondeada; la
 * misma rejilla que ya aparecía otras tres veces en la página. Ahora es una
 * composición editorial: fotografía y relato a la izquierda, los servicios
 * como una LISTA con filetes a la derecha.
 *
 * Las cifras de credibilidad (500 empresarios, 20 años) vivían en una barra de
 * métricas gigantes. Se movieron al párrafo: un número dentro de una frase se
 * lee como un hecho; el mismo número a 48 px se lee como publicidad.
 */
const Academy: React.FC = () => {
    const { t } = useIdioma();
    /* El barrido direccional del hover vive aquí, sobre la lista de
       servicios. Ver useHoverDireccional en motion.ts. */
    const filas = useHoverDireccional<HTMLUListElement>();

    const scope = useSectionMotion<HTMLElement>(({ gsap, scope, reveal, full, titular }) => {
        titular({ start: 'top 74%' });

        /* Las dos piezas de la fotografía, buscadas UNA vez. Estaban repetidas
           en tres llamadas distintas, y esa dispersión es justo lo que dejó
           pasar que el marcado perdiera los marcadores sin que nadie lo viera.

           A propósito SIN comprobar que existan: si un día vuelven a faltar,
           que GSAP grite. Las otras dos animaciones muertas de esta página
           llevaban meses calladas precisamente porque iban por caminos que
           tragan el vacío en silencio. */
        const marco = scope.querySelector('[data-marco-foto]');
        const foto = scope.querySelector('[data-foto]');

        /* Cortina sobre la fotografía, igual que en las bandas a sangre. */
        gsap.from(marco, {
            clipPath: 'inset(0% 0% 100% 0%)',
            duration: full ? 1.2 : 0.7,
            ease: full ? 'power3.inOut' : EASE.out,
            scrollTrigger: { trigger: marco, start: 'top 88%' },
        });

        reveal('[data-fade]', { start: 'top 70%' });

        /* Las filas entran una tras otra: se leen como renglones de una lista
           que se va escribiendo, no como seis cajas que aparecen a la vez. */
        reveal('[data-fila]', {
            trigger: scope.querySelector('[data-lista]'),
            start: 'top 80%',
            y: 16,
            duration: 0.6,
            stagger: 0.075,
        });

        /* El mismo «acomodo» de las bandas a sangre, con temporizador propio y
           repitiéndose en cada visita. Ver la nota larga en FotoBanda.tsx. */
        gsap.fromTo(
            foto,
            { scale: full ? 1.18 : 1.10 },
            {
                scale: 1,
                duration: full ? 1.7 : 1.1,
                ease: EASE.asiento,
                scrollTrigger: {
                    trigger: marco,
                    start: 'top 90%',
                    end: 'bottom 10%',
                    toggleActions: REPITE,
                },
            },
        );
    });

    return (
        <section
            ref={scope}
            id="academy"
            className="rp-on-light bg-acero-100 px-5 py-[clamp(5rem,11vw,9rem)] sm:px-8"
        >
            <div className="mx-auto max-w-[1240px]">
                <div className="grid gap-x-16 gap-y-14 lg:grid-cols-12">
                    {/* Texto arriba y fotografía debajo, no al revés: con la foto
                        primero la columna izquierda terminaba 300 px por debajo de
                        la lista y el fold quedaba descuadrado. */}
                    <div className="lg:col-span-5">
                        <h2 className="rp-h2 max-w-[15ch] text-navy-950">
                            <Frase marca="titular">{t('lp.academy.titular')}</Frase>
                        </h2>
                        <p data-fade className="rp-body mt-6 text-acero-700">
                            {t('lp.academy.parrafo1')}
                            <strong className="font-semibold text-navy-950">
                                {t('lp.academy.parrafoMarca')}
                            </strong>
                            {t('lp.academy.parrafo2')}
                        </p>

                        {/* El marco va en acero, no en navy: durante el
                            revelado la zona todavía sin cubrir SE VE, y un
                            rectángulo azul saturado apareciendo sobre un fold
                            claro se lee como un error de carga. En un gris de la
                            familia se lee como el hueco de la foto. */}
                        <div
                            data-marco-foto
                            className="mt-10 overflow-hidden rounded-2xl bg-acero-200"
                        >
                            {/* Los dos marcadores van en divs y NO en
                                `<ReveladoFluido>`: TypeScript acepta un `data-*`
                                en un componente propio y luego lo tira en
                                silencio — la misma trampa anotada en FotoBanda.
                                Ya costó las dos animaciones de este fold, que
                                estuvieron muertas desde que la <img> suelta se
                                cambió por el revelado.

                                Y `data-foto` envuelve al componente entero en
                                vez de marcar su <img>: dentro hay una <img> y un
                                <canvas> superpuestos, y escalar solo a uno los
                                desincroniza. */}
                            <div data-foto>
                                <ReveladoFluido
                                    foto={CUADRILLA}
                                    sizes="(min-width: 1024px) 40vw, 100vw"
                                    className="aspect-[4/3] w-full object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    <div data-lista className="lg:col-span-6 lg:col-start-7">
                        <ul ref={filas} className="border-b border-acero-200">
                            {SERVICIOS.map((s) => (
                                /* El filete divisorio vive en el <li> y va de lado a
                                   lado; el fondo redondeado del hover vive en el <a>.
                                   Juntos en el mismo elemento, el borde superior
                                   heredaba las esquinas redondeadas y las seis filas
                                   volvían a leerse como seis tarjetas. */
                                <li key={s.clave} data-fila className="border-t border-acero-200">
                                    <a
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative isolate -mx-4 flex items-start justify-between gap-6 overflow-hidden rounded-xl px-4 py-5"
                                    >
                                        {/* Superficie del hover. Por defecto se funde con CSS,
                                            que es lo que ven el dedo, el teclado y quien pidió
                                            movimiento reducido. Con ratón, useHoverDireccional
                                            toma el mando y la hace ENTRAR por el canto que
                                            cruzaste. Va detrás del texto (-z-10 dentro del
                                            `isolate` del enlace) y no recibe punteros. */}
                                        <span
                                            data-superficie
                                            aria-hidden="true"
                                            className="absolute inset-0 -z-10 bg-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
                                        />
                                        <span className="min-w-0">
                                            <span className="rp-fila-titulo block text-[17px] tracking-tight text-navy-950">
                                                {t(`${s.clave}.titulo` as Parameters<Traducir>[0])}
                                            </span>
                                            <span className="mt-1 block text-[14.5px] leading-relaxed text-acero-700">
                                                {t(`${s.clave}.texto` as Parameters<Traducir>[0])}
                                            </span>
                                        </span>
                                        <ArrowUpRight
                                            aria-hidden="true"
                                            className="mt-1 h-[18px] w-[18px] shrink-0 text-acero-400 transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-navy-950"
                                        />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Programa insignia. Es la puerta de entrada a la Academy, así que
                    se lleva su propia superficie navy en vez de ser una fila más. */}
                <div
                    data-fade
                    data-fondo="navy"
                    className="mt-[clamp(3.5rem,7vw,5.5rem)] grid gap-8 rounded-2xl bg-navy-850 p-8 sm:p-12 lg:grid-cols-12 lg:items-center"
                >
                    <div className="lg:col-span-8">
                        <h3 className="rp-h3 text-white">{t('lp.academy.programa.titulo')}</h3>
                        <p className="rp-body mt-4 text-acero-300">
                            {t('lp.academy.programa.texto')}
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:justify-self-end">
                        <a
                            href="https://dechalanaempresario.com/landing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rp-btn rp-btn--outline w-full sm:w-auto"
                        >
                            {t('lp.academy.programa.boton')}
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Academy;
